<template>
  <transition-group name="board" tag="section" id="board">
    <div
      v-for="(row, rowIndex) in status.boardData.slice().reverse()"
      :key="reverseIndex(rowIndex)"
      :aria-label="getRowAriaLabel(row, reverseIndex(rowIndex))"
      @click="toggleSelectedRow(reverseIndex(rowIndex))"
      :class="{
        selected: row.isSelected,
        locked: row.isLocked,
        unlocked: !row.isLocked,
        'not-entered': !row.playerFound & row.isLocked,
      }"
      class="board-row"
    >
      <div
        class="board-cell"
        v-for="(value, columnIndex) in row.sequence"
        :key="columnIndex"
        :class="{
          focal: isFocal(value, reverseIndex(rowIndex)),
        }"
      >
        <div
          class="shape"
          :class="highlightShape(value, row, columnIndex)"
          :id="isCursorID(row, columnIndex)"
          :style="isHintRow(row)"
        ></div>
        <div class="value" :class="highlightValue(columnIndex, row)">
          {{ value }}
        </div>
        <transition name="fade">
          <div
            class="operator"
            v-html="errorArray[columnIndex].operator"
            v-if="errorArray[columnIndex].operator && !row.isLocked"
          ></div>
        </transition>
      </div>
    </div>
  </transition-group>
</template>

<script>
import { STATUS, MODE } from "../types.js";
export default {
  name: "KnotBoard",
  data() {
    return {
      sequenceLength: 4,
      sixSevenActive: false,
    };
  },
  props: {
    status: {
      type: Object,
      required: true,
    },
    intersectionSet: {
      type: Set,
      required: true,
    },
    errorArray: {
      // Array with details about errors
      type: Array,
      required: false,
    },
    mode: {
      type: String,
      required: true,
    },
    focalOn: {
      type: Boolean,
      required: true,
    },
    reverseFill: {
      type: Boolean,
      required: true,
    },
    sixSevenPossible: {
      type: Boolean,
      required: true,
    },
  },
  watch: {
    isSixSeven(value) {
      if (value) {
        this.sixSevenActive = true;
        setTimeout(() => {
          this.sixSevenActive = false;
        }, 1500);
      }
    },
  },
  computed: {
    totalRows() {
      return this.status.boardData.length;
    },
    cursorPosition() {
      const lastRow = this.status.boardData[this.status.boardData.length - 1];
      const filledCount = lastRow.sequence.filter(
        (value) => value != null
      ).length;
      return this.reverseFill
        ? this.sequenceLength - filledCount - 1
        : filledCount;
    },
    isSixSeven() {
      if (this.status.boardData.length === 0) return false;
      const lastRow = this.status.boardData[this.status.boardData.length - 1];
      const index6 = lastRow.sequence.indexOf(6);
      const index7 = lastRow.sequence.indexOf(7);
      return index6 !== -1 && index7 !== -1 && index6 + 1 === index7;
    },
  },
  methods: {
    getRowAriaLabel(row, rowIndex) {
      const rowString = row.sequence.filter((item) => item !== null).join(", ");
      if (!row.isLocked) {
        if (rowString === "") {
          return `row ${rowIndex} blank`;
        }
        return `entered ${rowString} at row ${rowIndex}`;
      } else if (row.isSelected) {
        return `sequence ${rowString} selected at row ${rowIndex}`;
      } else {
        return `sequence ${rowString} at row ${rowIndex}`;
      }
    },
    reverseIndex(rowIndex) {
      return this.totalRows - rowIndex - 1;
    },
    isFocal(value, rowIndex) {
      if (
        !this.focalOn ||
        this.errorArray.some((item) => item.operator !== null)
      ) {
        return false;
      }

      return (
        this.intersectionSet.has(value) &&
        this.status.boardData[rowIndex].isLocked &&
        this.status.boardData[rowIndex].isSelected
      );
    },
    isHintRow(row) {
      if (row.isHint) {
        return {
          backgroundImage: "url('images/lightbulbfilled_bg.svg') !important",
          backgroundSize: "50% !important",
        };
      }
      return {};
    },
    highlightValue(columnIndex, row) {
      if (row.isLocked) {
        return;
      }
      return { "highlight-text": this.errorArray[columnIndex].highlightValue };
    },
    isCursorID(row, columnIndex) {
      if (row.isLocked) {
        return;
      }

      if (columnIndex === this.cursorPosition) {
        return "cursor";
      }
    },
    highlightShape(value, row) {
      let classes = new Object();
      if (row.isLocked && row.isSelected) {
        if (this.status.puzzleStatus === STATUS["COMPLETE"]) {
          classes["red-herring-background"] = this.isRedHerring(value);
          classes["knot-background"] = value === this.status.puzzleInfo.knot;
        } else if (this.mode === MODE["EASY"]) {
          if (row.isSelected && this.intersectionSet.has(value)) {
            classes["knot-background"] = true;
          }
        }
      }
      let sixSevenArray = [6,7]
      if (
        this.sixSevenPossible &&
        sixSevenArray.includes(value) &&
        this.sixSevenActive &&
        sixSevenArray.every((required) => row.sequence.includes(required))
      ) {
        classes[`six-seven-${value}`] = true;
      }
      return classes;
    },
    isRedHerring(value) {
      if (this.status.puzzleInfo === null) {
        return false;
      }
      return this.status.puzzleInfo.red_herrings.indexOf(value) >= 0;
    },
    toggleSelectedRow(rowIndex) {
      this.$emit("row-tapped", rowIndex);
    },
  },
};
</script>

<style scoped>
* {
  font-family: "Fira Sans", system-ui;
}
#board {
  flex-direction: column;
  row-gap: var(--board-spacing);
  justify-content: center;
  align-items: center;
  overflow: hidden;
}

.board-row {
  display: flex;
  column-gap: var(--board-spacing);
  justify-content: center;
  align-items: stretch;
}

.board-cell {
  display: flex;
  aspect-ratio: 1/ 1;
  transition: all 0.125s cubic-bezier(0.94, 0.77, 0.57, 1.25);
  height: calc(var(--board-cell-dimension));
  font-size: calc(var(--board-cell-dimension) * 0.5);
  justify-content: center;
  align-items: center;
  user-select: none;
  font-weight: bold;
  position: relative;
  touch-action: manipulation;
}

.focal .shape {
  border: 0.45vh solid var(--red-herring-color);
  transition: border 0.6s cubic-bezier(0.94, 0.77, 0.57, 1.25), all 0.3s ease;
}

#cursor {
  border: 0.125vh solid var(--text-color);
}

.shape {
  width: 100%;
  height: 100%;
  transform-origin: center; /* Ensures rotation happens around the center */
  position: absolute;
  background-color: var(--board-cell-color);
  border-radius: 5px;
  transition: all 0.3s ease; /* Smooth transition */
  border: 0.45vh solid rgba(0, 0, 0, 0);
}
.six-seven-6 {
  animation: six-seven-left 1.5s ease-in-out 1;
}
.six-seven-7 {
  animation: six-seven-right 1.5s ease-in-out 1;
}

.value {
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
  width: 100%;
  height: 100%;
  font-weight: bold;
  transition: color 0.6s ease;
}

.dark .locked .value {
  color: var(--background-color);
}
.board-space {
  display: flex;
  flex: 1;
}

.board-cell.locked {
  cursor: pointer;
}

.selected .board-cell {
  height: calc(var(--board-cell-dimension) * 1.125);
  font-size: calc(var(--board-cell-dimension) * 0.5 * 1.125);
}

.unlocked .board-cell {
  height: calc(var(--board-cell-dimension) * 0.9);
  font-size: calc(var(--board-cell-dimension) * 0.5 * 0.9);
}
.unlocked .shape {
  background-color: var(--input-cell-color);
}
.selected .shape {
  background-color: var(--selected-color);
}
.board-enter-active,
.board-leave-active {
  transition: all 0.125s cubic-bezier(0.94, 0.77, 0.57, 1.25);
}
.board-enter-from .board-cell,
.board-leave-to .board-cell {
  font-size: 0;
  height: 0px;
}

.red-herring-background {
  transform: rotate(405deg); /* Rotate to diamond */
  width: 70.7107%;
  height: 70.7107%;
}

.knot-background {
  border-radius: 50%;
  transform: rotate(720deg); /* Rotate to diamond */
}
.knot-background,
.red-herring-background {
  transition: all 1.5s ease, border 0.3s ease;
}

.not-entered .value {
  color: var(--board-cell-color) !important;
  text-shadow: 1px 0 #333, -1px 0 #333, 0 1px #333, 0 -1px #333, 1px 1px #333,
    -1px -1px #333, 1px -1px #333, -1px 1px #333;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.6s ease;
}

.highlight-text {
  color: var(--red-herring-color);
  transition: color 0.6s ease;
  animation: blink 2s cubic-bezier(0.94, 0.77, 0.57, 1.25) infinite;
}

.operator {
  width: 40%;
  aspect-ratio: 1 / 1;
  position: absolute;
  right: 0;
  top: 50%;
  transform: translate(calc(50% + var(--board-spacing) / 2), -50%);
  z-index: 50;
  transition: all 0.6s cubic-bezier(0.94, 0.77, 0.57, 1.25);
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}
@keyframes six-seven-left {
  0% {
    transform: skew(0);
  }
  20% {
    transform: skew(0, 10deg);
  }
  40% {
    transform: skew(0, -10deg);
  }
  60% {
    transform: skew(0, 10deg);
  }
  80% {
    transform: skew(0, -10deg);
  }
  100% {
    transform: skew(0);
  }
}
@keyframes six-seven-right {
  0% {
    transform: skew(0);
  }
  20% {
    transform: skew(0, -10deg);
  }
  40% {
    transform: skew(0, 10deg);
  }
  60% {
    transform: skew(0, -10deg);
  }
  80% {
    transform: skew(0, 10deg);
  }
  100% {
    transform: skew(0);
  }
}
@keyframes blink {
  0% {
    transform: scale(1);
  }
  25% {
    transform: scale(1.15);
  }
  50% {
    transform: scale(0.875);
  }
  75% {
    transform: scale(1.15);
  }
  100% {
    transform: scale(1);
  }
}
</style>